import {Desk, UserActiveDeskFeat} from "lib/commons/desk";
import {EventsMgr, EventsMgrCb, IEventsMgr} from "lib/commons/events";
import {EHttpStatusCode, EndPointResolver, ERespFormat, IEndPoint, INetErrorHook, IO, IResolverPointer, IResponse, isEndPointErrorHookable, UrlQs} from "lib/commons/io/io";
import {IReg, REG} from 'lib/commons/registry';
import {ISecurityCtxPointer, SEC} from 'lib/commons/security';
import {AuthSrv, configAuthSrv, configUsersSrv, JUser, OAuthSrvConfig, OUsersSrvConfig, UsersSrv} from "lib/core/user";
import {configUserDatasSrv, DeskUserDatasSrv, OUserDatasSrvConfig, UserDatasSrv} from "lib/core/userDatas";
import {configUserSelfSrv, OUserSelfSrvConfig, UserSelfSrv} from "lib/core/userSelf";
import {ROLES} from "lib/commons/roles";
import {ERROR} from "lib/core/errorReport";
import {LANG} from "lib/commons/lang";

/** Configuration d'un univers. */
export interface OUniverseConfig {

	/** SubReg de REG.reg. null par défaut et reprend directement REG.reg. */
	reg?: IReg<IUniversePointer>

	/**
	 * Identifiant unique (par rapport aux autres univers déclarés) de l'univers.
	 * null si univers anonyme, non référencé dans UNIVERSE.all.
	 */
	id: string | null

	/**
	 * Code serveur de l'univers qui peut différer de l'id si plusieurs universe UI sont configurés pour le même univers serveur.
	 */
	srvCode?: string

	/** Nom de l'univers (affichable coté UI) */
	name?: string

	/** Racine de l'univers incluant une méthode d'authentification par défaut. */
	universeUrl: IEndPoint

	/** Racine des libs */
	libUrl?: IEndPoint

	/** Racine des skin */
	skinUrl?: IEndPoint

	/** Racine des ressources back */
	backUrl?: IEndPoint

	/** Racine des pages front */
	frontUrl?: IEndPoint

	/** Déclaration des HttpExecFrames de cet univers. */
	httpFrames?: Dict<OHttpExecFrameConfig>

	/**
	 * Déclaration des WsExecFrames de cet univers qui peut déclarer une config dédiée ou
	 * réexploiter un ExecFrame déjà instancié par une autre instance d'univers (qui pointe le même serveur,
	 * mais avec une config de registre différente).
	 */
	wsFrames?: Dict<OWsExecFrameConfig | IWsExecFrame>

	/** Mode desktop, ie server local sans réelle gestion de users, paths disques potentiellement accessibles. */
	isDesktopApp?: boolean
}

/** Configuration d'un execFrame http. */
export interface OHttpExecFrameConfig {

	/** url + méthode d'auth ou méthode d'auth seule (l'URL sera construite automatiquement avec l'URL de l'univers et la key de l'execFrame). */
	execFrameUrl?: IEndPoint
}

/** Configuration d'un execFrame webSocket. */
export interface OWsExecFrameConfig {

	execFrameUrl?: IEndPoint

	/**
	 * Mode de préservation de la connexion webSocket.
	 * - 'no' : pas de controle de la connexion websocket : déconnexion possible en fonction des timeouts des proxy / serveurs.
	 * - 'indefinitely' : la connexion est maintenue tant que la connexion internet est valide. En cas de déconnexion,
	 *    des tentatives de reconnexion seront réalisées périodiquement.
	 * - 'userActive' : dépend de l'activité du user piloté par UserActiveDeskFeat et son paramètre 'activeTimeout'.
	 *      Lorsque UserActiveDeskFeat juge le user :
	 *      - 'inactive', le mode passe en mode 'no' (la connexion sera interrompue)
	 *      - 'active', le mode passe en mode 'indefinitely' (la connexion est maintenue opérationnelle)
	 *      Si la feature UserActiveDeskFeat n'a pas été activée sur le desk, le mode est 'indefinitely'.
	 * - 'onListening' : démarre et maintenue active dès le 1er abonnement au msgListeners ET tant que le user
	 * 			est considéré actif par le UserActiveDeskFeat. La connexion est coupée si plus aucun listener est
	 * 			abonné aux messages de ce WS OU si le user est jugé inactif.
	 */
	keepAlive?: 'userActive' | 'onListening' /* par défaut */ | 'indefinitely' | 'no'

	proxyTo?: WsExecFrame
}


export class Universe {

	reg: IReg<IUniversePointer>;

	config: OUniverseConfig;

	httpFrames: Dict<HttpExecFrame>;

	wsFrames: Dict<IWsExecFrame>;

	constructor(config: OUniverseConfig) {
		if (config.id) {
			UNIVERSE.all.set(config.id, this); //univers non anonyme
			if (!config.srvCode) config.srvCode = config.id;
		}
		this.buildConfig(config);
		this.buildUniverse(config);
		this.linkUniverse()
	}

	/**
	 * Identifiant unique (par rapport aux autres univers déclarés) de l'univers.
	 * null si univers anonyme, non référencé dans UNIVERSE.all.
	 */
	public getId(): string {
		return this.config.id;
	}

	/** Nom affichable caractérisant l'univers */
	public getName(): string {
		return this.config.name || this.config.id;
	}

	/** 1ère étape : complète la config de l'univers. */
	protected buildConfig(config: OUniverseConfig) {}

	/** 2ème étape : instancie les composants et services de l'univers. */
	protected buildUniverse(config: OUniverseConfig) {
		this.config = config;
		this.reg = config.reg || REG.reg;
		if (!this.reg) console.trace("No reg found");
		this.httpFrames = {};
		if (config.httpFrames) for (const key in config.httpFrames) this.httpFrames[key] = new HttpExecFrame(this, key, config.httpFrames[key]);
		if (config.wsFrames) {
			this.wsFrames = {};
			for (const key in config.wsFrames) {
				const ws = config.wsFrames[key];
				if (ws instanceof WsExecFrame || ws instanceof WsProxyExecFrame) {
					this.wsFrames[key] = ws;
				} else if ((ws as OWsExecFrameConfig).proxyTo) {
					this.wsFrames[key] = new WsProxyExecFrame(this.getId(), (ws as OWsExecFrameConfig).proxyTo);
				} else {
					this.wsFrames[key] = new WsExecFrame(this, key, ws as OWsExecFrameConfig);
				}
			}
		}
		this.reg.env.resolver = new EndPointResolver()
			.addEndPoint("skin", config.skinUrl)
			.addEndPoint("back", config.backUrl)
			.addEndPoint("lib", config.libUrl)
			.addEndPoint("front", config.frontUrl);
	}

	/**
	 * 3ème étape : relie les composants et services de l'univers entre eux,
	 * affecte les env du registre, enrichi les étapes de chargement du desk...
	 */
	protected linkUniverse() {
		this.reg.env.resolver.freeze();
		this.reg.env.universe = this;
	}

}

export interface IUniversePointer extends IResolverPointer {
	universe: Universe
}

/**
 * Univers disposant de 2 execFrame 'public' et 'web' avec les svcs
 * - d'authentification
 * - de gestion de son compte personnel: userSelf.
 * - d'accès à ses données personnelles (opionnel): userDatas.
 */
export interface OBasicUniverseConfig extends OUniverseConfig {
	/** ExecFrames http standards. */
	httpFrames?: {
		public?: OHttpExecFrameConfig
		web?: OHttpExecFrameConfig
	}

	/** ExecFrames webSocket standards. */
	wsFrames?: {
		ws?: OWsExecFrameConfig | IWsExecFrame
	}

	/** Config du service d'auth, ou service d'auth déjà condfiguré.  */
	auth?: OAuthSrvConfig
	authSrv?: AuthSrv

	/** Services d'accès aux users. */
	userSelf?: OUserSelfSrvConfig
	userDatas?: OUserDatasSrvConfig | UserDatasSrv

	/** Accès aux logs d'admin. */
	adminLogsUrl?: IEndPoint;

	/** Accès au svc remoteContent */
	remoteContentUrl?: IEndPoint;

	/** Accès au svc batch */
	batchUrl?: IEndPoint;

	/** Accès au service générique de communication inter-users (WebSocket). */
	liaiseUrl?: IEndPoint;
	maintenanceUrl?: IEndPoint;
}

export class BasicUniverse extends Universe implements INetErrorHook {

	config: OBasicUniverseConfig;

	reg: IReg<IUniverseEnv>;

	auth: AuthSrv;

	userSelf: UserSelfSrv;

	userDatas: UserDatasSrv;

	httpFrames: {
		public: HttpExecFrame
		web: HttpExecFrame
	};

	wsFrames: {
		ws?: IWsExecFrame
	};


	get clId(): string {
		let clId = this._clId;
		if (!clId) {
			//En priorité on prend le clId du WsFrame qui peut être mutualisé entre plusieurs instances d'univers.
			clId = this.wsFrames.ws?.clId;
			if (!clId) clId = (Math.floor(Math.random() * Math.floor(Number.MAX_SAFE_INTEGER)) + 1).toString(36); // A défaut, on en génère un.
			this._clId = clId;
		}
		return clId;
	}

	protected _clId: string

	constructor(config: OBasicUniverseConfig) {
		super(config);
	}

	buildConfig(config: OBasicUniverseConfig) {
		let httpFrames = config.httpFrames;
		if (!httpFrames) config.httpFrames = httpFrames = {};
		if (!httpFrames.public) {
			//httpFrames.public = {execFrameUrl: new PublicEndPoint(config.universeUrl.resolve("public/").url)}; NON car le svc login fait le setCookie => credentials 'include' obligatoire pour la response.
			httpFrames.public = {execFrameUrl: config.universeUrl.resolve("public/")};
		}
		if (isEndPointErrorHookable(httpFrames.public.execFrameUrl)) httpFrames.public.execFrameUrl.setErrorHook(this);
		if (!httpFrames.web) httpFrames.web = {execFrameUrl: config.universeUrl.resolve("web/")};
		if (isEndPointErrorHookable(httpFrames.web.execFrameUrl)) httpFrames.web.execFrameUrl.setErrorHook(this);
		config.auth = configAuthSrv(httpFrames.web.execFrameUrl, httpFrames.public.execFrameUrl, config.auth);
		config.userSelf = configUserSelfSrv(httpFrames.public.execFrameUrl, httpFrames.web.execFrameUrl, config.userSelf);
		config.userDatas = configUserDatasSrv(httpFrames.web.execFrameUrl, config.userDatas);
		if (!("adminLogsUrl" in config)) config.adminLogsUrl = httpFrames.web.execFrameUrl.resolve("u/adminLogs");
		if (config.wsFrames?.ws && !("liaiseUrl" in config)) config.liaiseUrl = httpFrames.web.execFrameUrl.resolve("u/liaise");
		if (!("maintenanceUrl" in config)) config.maintenanceUrl = httpFrames.web.execFrameUrl.resolve("u/maintenance");
		if (!("batchUrl" in config)) config.batchUrl = config.httpFrames.web.execFrameUrl.resolve("u/batch");
		if (!("remoteContentUrl" in config)) config.remoteContentUrl = config.httpFrames.web.execFrameUrl.resolve("u/remoteContent");
	}

	buildUniverse(config: OBasicUniverseConfig) {
		super.buildUniverse(config);
		this.auth = config.authSrv || new AuthSrv(config.auth, this);
		this.auth.listeners.on('loggedUserChanged', (oldUser: JUser, newUser: JUser) => {
			this.createSecurityCtx(newUser)
		}, -100); // Avant tous les autres traitements pour mettre à dispo le SecurityCtx
		this.userSelf = new UserSelfSrv(config.userSelf);
		if (config.userDatas) {
			if (config.userDatas instanceof UserDatasSrv) {
				this.userDatas = config.userDatas;
			} else {
				this.userDatas = new DeskUserDatasSrv(config.userDatas);
				this.userDatas.connectToAuth(this.auth);
			}
			this.reg.setPersistUserStates(this.userDatas);
		}
		this.reg.env.resolver
			.addEndPoint("web", this.httpFrames.web.config.execFrameUrl)
			.addEndPoint("public", this.httpFrames.public.config.execFrameUrl);
	}

	linkUniverse() {
		super.linkUniverse();
		this.userSelf.connectToAuth(this.auth);
		const ws = this.wsFrames?.ws;
		if (ws) {
			ws.listenAuth(this.auth);
			this.userDatas?.connectToWs(ws);
		}
	}

	onEndPointError(resp: IResponse) {
		if (resp.status === EHttpStatusCode.forbidden) {
			//On recontrole la connection du user
			this.auth.fetchUser();
		} else if (resp.status === EHttpStatusCode.serviceUnvailable && this.reg.getSvc("maintenanceCb")) {
			resp.text().then((t: string) => {
				const m = JSON.parse(t) as { maintenanceMsg: { lang: string, label: string }[] };
				if (m.maintenanceMsg) this.reg.getSvc<IMaintenanceCb>("maintenanceCb")(this, m.maintenanceMsg);
			}).catch((e) => {});
		}
	}

	async isSecFetchSiteAware(): Promise<boolean> {
		const result = await this.httpFrames.public.config.execFrameUrl.fetchJson<{ 'Sec-Fetch-Site': boolean }>("_/isSecFetchAware");
		return result && result["Sec-Fetch-Site"];
	}

	/** Création d'un contexte de sécurité pour cet univers. */
	protected createSecurityCtx(newUser: JUser) {
		this.reg.env.securityCtx = newUser ? new SEC.SecurityCtx(newUser.account, ROLES.resolveUserRoles(newUser), newUser.isSuperAdmin, this) : null;
	}

}

export type IMaintenanceCb = (universe: BasicUniverse, msg: { lang: string, label: string }[]) => any;

export interface IBasicUniversePointer extends IUniversePointer {
	universe: BasicUniverse
}

export type IUniverseEnv = IBasicUniversePointer & ISecurityCtxPointer;


/**
 *
 */
export interface OCoreUniverseConfig extends OBasicUniverseConfig {

	adminUsers?: OUsersSrvConfig;

	useUsers?: OUsersSrvConfig;

}


export class CoreUniverse extends BasicUniverse {

	config: OCoreUniverseConfig;

	adminUsers: UsersSrv;

	useUsers: UsersSrv;


	buildConfig(config: OCoreUniverseConfig) {
		super.buildConfig(config);
		config.adminUsers = configUsersSrv(config.httpFrames.web.execFrameUrl, config.adminUsers, true);
		config.useUsers = configUsersSrv(config.httpFrames.web.execFrameUrl, config.useUsers, false);
	}

	buildUniverse(config: OCoreUniverseConfig) {
		super.buildUniverse(config);
		this.adminUsers = new UsersSrv(config.adminUsers);
		this.useUsers = new UsersSrv(config.useUsers);
	}

	linkUniverse() {
		super.linkUniverse();
		this.adminUsers.connectToAuth(this.auth);
		this.useUsers.connectToAuth(this.auth);
	}

}

export interface ICoreUniversePointer extends IUniversePointer {
	universe: CoreUniverse
}

export type ICoreUniverseEnv = ICoreUniversePointer & ISecurityCtxPointer;


/**
 *
 */
export class HttpExecFrame {

	constructor(public readonly universe: Universe, public readonly key: string, public readonly config: OHttpExecFrameConfig) {
		if (!config.execFrameUrl) config.execFrameUrl = universe.config.universeUrl.resolve(key);
		else if (!config.execFrameUrl.url) config.execFrameUrl = config.execFrameUrl.resolve(IO.resolveUrl(key, universe.config.universeUrl.url));
	}

	/**
	 * Interrogation générique de tout service via cet execFrame retournant un Promise<IResponse> (catch rejection inutile).
	 *
	 * @param servicePath code du service (ex: "u/adminUser"), éventuellement suffixé d'un path (ex: "u/dav/myFolder"), mais sans QueryString.
	 * @param params liste des paramètres à encoder ou paramètres déjà encodés sous la forme : A=x&B=y
	 * @param si reqInit.method='POST' et reqInit.body==null, les paramètres seront passés en POST avec le content-type "application/x-www-form-urlencoded"
	 */
	fetchSvc(servicePath: string, cdaction?: string, params?: string | Dict<any> | URLSearchParams, format?: ERespFormat, reqInit?: RequestInit): Promise<IResponse> {
		return this.config.execFrameUrl.fetch(buildRequest(servicePath, cdaction, params, reqInit), format, reqInit);
	}

	/**
	 * Interrogation générique de tout service via cet execFrame retournant une promesse d'un json typé R.
	 * Promesse rejetée à catcher en cas d'anomalie.
	 *
	 * @param servicePath code du service (ex: "u/adminUser"), éventuellement suffixé d'un path (ex: "u/dav/myFolder"), mais sans QueryString.
	 * @param params liste des paramètres à encoder ou paramètres déjà encodés sous la forme : A=x&B=y
	 * @param si reqInit.method='POST' et reqInit.body==null, les paramètres seront passés en POST avec le content-type "application/x-www-form-urlencoded"
	 */
	fetchSvcJson<R>(servicePath: string, cdaction?: string, params?: string | Dict<any> | URLSearchParams, reqInit?: RequestInit): Promise<R> {
		return this.config.execFrameUrl.fetchJson<R>(buildRequest(servicePath, cdaction, params, reqInit), reqInit);
	}

	/**
	 * Construit un endPoint avec ces paramètres d'interrogation d'un service.
	 */
	getEndPoint(servicePath: string, cdaction?: string, params?: string | Dict<any> | URLSearchParams): IEndPoint {
		return this.config.execFrameUrl.resolve(buildRequest(servicePath, cdaction, params));
	}

}

/** Events du cycle de vie d'une session WebSocket. */
type IWsLcEvents = {
	opened: () => void
	error: () => void
	closed: (code: number, reason: string, wasClean: boolean) => void
}

/** Events reltifs à l'arrivée d'un msg WebSocket. */
type IWsEvents = {
	[svc: string]: (m: Jsonisable) => void
}

/** Etat d'une session WebSocket */
export const enum EWsState {
	wsConnecting = 0,
	wsOpened = 1,
	wsClosing = 2,
	wsClosed = 3
}

/** Abstraction de la classe native WebSocket en vue d'une impl centralisée dans un SharedWorker. */
export interface IWsExecFrame {
	lcListeners: IEventsMgr<IWsLcEvents>
	msgListeners: IEventsMgr<IWsEvents>

	wsState: EWsState

	keepAlive: boolean

	/** Identificant unique de ce client. */
	clId: string

	startWs(): void

	stopWs(code: number, reason: string): void

	/** Envoie un msg, Error si la webSocket n'est pas démarrée. */
	sendMsg(m: Jsonisable): void

	/** Envoie un msg, en s'assurant que la webSocket est démarrée. */
	postMsg(m: Jsonisable): void

	/** Envoi une requete avec sa réponse promise. Promise rejetée si la webSocket n'est pas démarrée */
	sendReq<R>(m: Jsonisable): Promise<R>

	/** Ecoute le serveur d'auth pour forcer la deconnexion / reconnexion. */
	listenAuth(authServer: AuthSrv): void
}

/** Msg WebSocket de remontée d'erreur suite à l'envoi d'un msg pour un svc donné. */
export interface JWsMsgError {
	svc: string
	error: Jsonisable
	msg: Jsonisable
	stack?: Jsonisable
}

export function isWsMsgError(m: any): m is JWsMsgError {return m && ('error' in m)}


/** Impl de base d'accès à un WebSocket vers les services d'un univers. */
export class WsExecFrame implements IWsExecFrame {

	static DEBUG = false;

	static switchProtocol(url: string) {return url.replace(/^http/, "ws")}

	static MSG_PING = Object.freeze({});

	get wsState(): EWsState {return this._starting ? EWsState.wsConnecting : this._ws ? this._ws.readyState : EWsState.wsClosed}

	/** Listeners du cycle de vie du WebSocket. */
	lcListeners = new EventsMgr<IWsLcEvents>();

	/** Listeners des messages filtrés selon le svc émetteur du message. */
	msgListeners: EventsMgr<IWsEvents>;

	/** Cb pour les EventsMgrCb des msgListeners */
	protected msgListenersCb: () => void;

	/** Liste des WsProxyExecFrame enregistrés. */
	wsProxies?: WsProxyExecFrame[];

	/** BuildId du server de la dernière connexion. Si le buildId diffère un reload complet de la page est nécessaire. */
	lastServerBuildId?: string;

	/** Assure de préserver la connexion indépendamment de tout timeout de la connexion WebSocket. */
	get keepAlive(): boolean {return typeof this._keepAlive === 'number'}

	set keepAlive(keepAlive: boolean) {
		if (keepAlive) {
			if (!this.keepAlive) {
				//on (ré)active le cycle de check
				this._keepAlive = window.setInterval(() => {
					if (this._flagSent) {
						//un msg a été envoyé après le dernier controle.
						this._flagSent = false;
					} else {
						//aucun msg envoyé, on force un ping.
						if (this.wsState === EWsState.wsOpened) this.sendMsg(WsExecFrame.MSG_PING);
						else this.startWs(); //on tente la réouverture de la connexion.
					}
				}, this.keepAliveInterval);
				this._flagSent = false;
			}
		} else if (this.keepAlive) {
			//on arrête le cycle de check.
			clearInterval(this._keepAlive);
			this._keepAlive = null;
		}
	}

	/** Interval de controle : doit être inférieur à la moitié du timeout de la connexion webSocket. */
	keepAliveInterval = 12000;//12s de cylce => un ping toutes les 24secondes

	/**
	 * Attention, un WsExecFrame peut-être mutualisé par plusieurs instances d'univers. Cet univers est celui
	 * qui est à l'origine de l'instanciation de ce WsExecFrame.
	 */
	universe: Universe;

	clId: string;

	protected _ws: WebSocket;

	/** Flag forcant l'état connecting de la répurétion du token jusqu'à l'obtention du msg d'init du serveur. */
	protected _starting: Promise<void>;

	/** Msgs en attente, pendant le processus d'init de la connexion. */
	protected _pending: string[];

	/** process en cours du setTimeout lorsque le keepAlive est actif. */
	protected _keepAlive: number | null;

	/** Flag indiquant qu'au moins un msg a été envoyé depuis le dernier check. */
	protected _flagSent = false;

	/** Si dépend d'un authServer pour la connexion. */
	protected _auth: AuthSrv;

	protected _reqIdCounter = 0;
	protected _respPromises: Map<number, IRespPending> = new Map();

	constructor(universe: Universe, public readonly key: string, public readonly config: OWsExecFrameConfig) {
		this.universe = universe;
		this.clId = (Math.floor(Math.random() * Math.floor(Number.MAX_SAFE_INTEGER)) + 1).toString(36);
		if (!config.execFrameUrl) config.execFrameUrl = universe.config.universeUrl.resolve(IO.resolveUrl(key, WsExecFrame.switchProtocol(universe.config.universeUrl.url)));
		else if (!config.execFrameUrl.url) config.execFrameUrl = config.execFrameUrl.resolve(IO.resolveUrl(key, WsExecFrame.switchProtocol(universe.config.universeUrl.url)));
		const keepAlive = config.keepAlive || 'onListening';
		const loadKey = `ws.${universe.getId()}#${key}`;
		if (keepAlive === 'userActive') {
			REG.reg.addToList(Desk.LC_load, loadKey, 1, () => {
				if (UserActiveDeskFeat.isIn(desk)) {
					desk.userActiveLstn.on('active', () => {
						this.keepAlive = true;
						this.startWs();
					});
					desk.userActiveLstn.on('inactive', () => {
						this.keepAlive = false;
						this.stopWs(1000, "user inactive");
					});
				}
				this.keepAlive = true;
				this.startWs();
			});
		} else if (keepAlive === 'indefinitely') {
			REG.reg.addToList(Desk.LC_load, loadKey, 1, () => {
				this.keepAlive = true;
				this.startWs();
			});
		} else if (keepAlive === 'onListening') {
			this.msgListenersCb = () => {
				const hasLstn = this.hasAnyMsgListener();
				if (hasLstn !== this.keepAlive) {
					this.keepAlive = hasLstn;
					if (hasLstn) this.startWs();
					else this.stopWs(1000, "no more listeners");
				}
			};
			this.msgListeners = new EventsMgrCb<IWsEvents>(this.msgListenersCb);
			REG.reg.addToList(Desk.LC_load, loadKey, 1, () => {
				if (UserActiveDeskFeat.isIn(desk)) {
					desk.userActiveLstn.on('active', () => {
						if (this.hasAnyMsgListener()) {
							this.keepAlive = true;
							this.startWs();
						}
					});
					desk.userActiveLstn.on('inactive', () => {
						this.keepAlive = false;
						this.stopWs(1000, "user inactive");
					});
				}
			});
		}
		if (!this.msgListeners) this.msgListeners = new EventsMgr<IWsEvents>();
	}

	protected hasAnyMsgListener() {
		if (this.msgListeners.hasAnyListeners()) return true;
		if (!this.wsProxies) return false;
		for (const wsProxy of this.wsProxies) {
			if (wsProxy.msgListeners.hasAnyListeners()) return true;
		}
		return false;
	}

	startWs(): Promise<void> {
		if (this._starting) return this._starting;
		const st = this._ws?.readyState;
		if (st === EWsState.wsOpened) {
			return Promise.resolve();
		} else if (st === EWsState.wsClosing) {
			//console.log("wsClosing:::", this.universe.getId(), this.clId);
			//si on est en cours de fermeture, on planifie le restart après la fermeture effective
			if (!this._needRestart) this._needRestart = new Promise((resolver) => {
				this.lcListeners.once("closed", () => {
					this._needRestart = null;
					resolver(this.startWs());
				})
			});
			return this._needRestart;
		}

		if (this._auth && this._auth.currentUser == null) return Promise.resolve(); //pas de user encore actif, pas de connexion possible.

		return this._starting = new Promise(async (resolve, reject) => {
			// let token: string;
			// try {
			// 	token = await this.universe.config.httpFrames.web.execFrameUrl.fetchText("u/userToken"); //FIXME execFrame 'web' en dur.
			// 	if (!token) {
			// 		this._starting = null;
			// 		reject("No user token");
			// 		return;
			// 	}
			// } catch (e) {
			// 	this._starting = null;
			// 	reject(e);
			// 	return;
			// }
			//console.log("wsUrl:::", this.config.execFrameUrl.url);
			try {
				this._ws = new WebSocket(this.config.execFrameUrl.url + IO.qs("clId", this.clId, /*"tk", token,*/ "proxy4Universes", this.wsProxies?.map((proxy) => proxy.universe).join('*')));
				if (WsExecFrame.DEBUG) {
					console.trace("ws created", this._ws.url);
					this._ws.onopen = (ev: Event) => {console.log("ws opened", this._ws.url)};
				}
				this._ws.onerror = (ev: Event) => {
					//this._ws = null; non nullifié sur le onclose.
					this._pending = null;
					if (this._starting) reject(ev);
					this.lcListeners.emit('error');
					ERROR.log("WebSocket error: " + ev);
				};
				this._ws.onclose = (ev: CloseEvent) => {
					if (ev.target !== this._ws) return; //Sécurité RaceCond
					if (WsExecFrame.DEBUG) console.log("ws closed", ev.reason, ev.wasClean, this._ws.url);
					this._ws = null;
					this._pending = null;
					if (this._starting) {
						this._starting = null;
						resolve();
					}
					for (const resp of this._respPromises.values()) {
						try {resp.reject(ev)} catch (e) {console.log(e)}
					}
					this._respPromises.clear();
					this.lcListeners.emit('closed', ev.code, ev.reason, ev.wasClean);
					if (ev.code > 1001) console.log("WebSocket close", ev);
				};
				this._ws.onmessage = (ev: MessageEvent) => {
					if (!this._ws) return; //Des messages semblent parfois envoyés après l'event onclose.
					try {
						const m = JSON.parse(ev.data);
						if ('reqId' in (m as JReqRespMsg)) {
							const pending = this._respPromises.get(m.reqId);
							if (pending) {
								this._respPromises.delete(m.reqId);
								if (isWsMsgError(m)) {
									pending.reject(m);
								} else {
									pending.resolve(m);
								}
							}
						} else if ('svc' in m) {
							if ('u' in (m as JProxiedMsg)) {
								const cd = (m as JProxiedMsg).u;
								if (this.universe.config.srvCode === cd) this.msgListeners.emitCatched(m.svc, m);
								if (this.wsProxies) for (const wsProxy of this.wsProxies) {
									if (wsProxy.universe === cd) wsProxy.msgListeners.emitCatched(m.svc, m);
								}
							} else {
								this.msgListeners.emitCatched(m.svc, m);
							}
						} else if ((m as JWelcomeMsg).buildId != null) {
							//Init
							try {
								//Check de la version du serveur
								if (!this.lastServerBuildId) {
									this.lastServerBuildId = (m as JWelcomeMsg).buildId;
								} else if (this.lastServerBuildId !== (m as JWelcomeMsg).buildId) {
									//Le serveur a été mis à jour, on reload toute la page.
									window.location.reload(true);
									return;
								}
								//Check de la version de statics
								const homeBuildId = (window as any).serverBuildId;
								if (homeBuildId && homeBuildId !== (m as JWelcomeMsg).buildId) {
									//La home static n'est pas à jour par rapport au serveur d'univers.
									const keyLastCheck = "lastBuildIdError_" + window.location.pathname + ":" + this.universe.getId();
									const lastCheck = sessionStorage.getItem(keyLastCheck);
									if (lastCheck && (Date.now() - Number.parseInt(lastCheck) < 30000)) {
										//On vient déjà de tenter de recharger, on stop un rechargement infini...
										sessionStorage.removeItem(keyLastCheck);
										ERROR.log("Server BuildId not equals between static home (" + homeBuildId + ") and server webSocket welcome msg (" + (m as JWelcomeMsg).buildId + ")");
										//On garde cette page... XXX on notifie le user de l'incohérence ?
									} else {
										sessionStorage.setItem(keyLastCheck, Date.now().toString()); //pour éviter un rechargement infini
										window.location.reload(true);
										return;
									}
								}
								if (this._starting) {
									this._starting = null;
									resolve();
								}
							} catch (e) {
								if (this._starting) {
									this._starting = null;
									resolve();
								}
								ERROR.log(e);
							}
							//Connexion valide.
							if (WsExecFrame.DEBUG) console.log("ws inited", this._ws.url, m);
							if (this._pending) {
								try {
									for (let i = 0; this._pending && i < this._pending.length; i++) {
										const m = this._pending[i];
										if (!m) continue;
										this._ws.send(m);
										this._pending[i] = null;
									}
									this._pending = null;
								} catch (e) {
									this.lcListeners.emit('error');
									ERROR.log(e);
								}
							}
							this.lcListeners.emit('opened');
						}
					} catch (e) {
						this._pending = null;
						this.lcListeners.emit('error');
						ERROR.log(e);
					}
				}
			} catch (e) {
				this._pending = null;
				if (this._starting) {
					this._starting = null;
					reject(e);
				}
				ERROR.log(e);
			}
		});
	}

	protected _needRestart?: Promise<void>;

	stopWs(code?: number, reason?: string): void {
		if (this._ws && this._ws.readyState < EWsState.wsClosing) this._ws.close(code, reason);
	}

	sendMsg(m: Jsonisable): void {
		this._ws.send(JSON.stringify(m));
		this._flagSent = true;
	}

	postMsg(m: Jsonisable) {
		if (this.wsState === EWsState.wsOpened) this.sendMsg(m);
		else {
			if (!this._pending) this._pending = [];
			this._pending.push(JSON.stringify(m)); //sérialisation pour bloquer toute modif avant envoi de m.
			this.startWs();
		}
	}

	async sendReq<R>(m: Jsonisable): Promise<R> {
		const reqId = ++this._reqIdCounter;
		(m as JReqRespMsg).reqId = reqId;
		this.sendMsg(m); //rejetée si la connection n'est pas active.
		return new Promise<any>((resolve, reject) => {
			this._respPromises.set(reqId, {resolve, reject});
		});
	}

	/** Ecoute le serveur d'auth pour forcer la deconnexion / reconnexion. */
	listenAuth(authServer: AuthSrv) {
		this._auth = authServer;
		authServer.listeners.on('loggedUserChanged', (oldUser: JUser, newUser: JUser) => {
			const restart = this.keepAlive || LANG.in(this.wsState, EWsState.wsOpened, EWsState.wsConnecting);
			this.stopWs(1000, "user disconnected");
			if (restart) this.startWs();
		})
	}

	addWsProxy(wsProxy: WsProxyExecFrame) {
		if (this.wsState !== EWsState.wsClosed) throw Error("WebSocket already started"); //todo restart la co ou gestion d'un msg pour l'exeFrame server d'ajout d'un proxy
		if (!this.wsProxies) this.wsProxies = [];
		this.wsProxies.push(wsProxy);
		this.lcListeners.on("opened", () => {
			wsProxy.lcListeners.emit("opened");
		});
		this.lcListeners.on("error", () => {
			wsProxy.lcListeners.emit("error");
		});
		this.lcListeners.on("closed", (code: number, reason: string, wasClean: boolean) => {
			wsProxy.lcListeners.emit("closed", code, reason, wasClean);
		});
		wsProxy.msgListeners = this.msgListenersCb ? new EventsMgrCb<IWsEvents>(this.msgListenersCb) : new EventsMgr<IWsEvents>();
	}
}

/** WsExecFrame qui redirige vers un autre WsExecFrame d'un autre univers. */
export class WsProxyExecFrame implements IWsExecFrame {
	lcListeners = new EventsMgr<IWsLcEvents>();
	msgListeners: EventsMgr<IWsEvents>;

	constructor(public universe: string, public target: WsExecFrame) {
		target.addWsProxy(this);
	}

	get clId(): string {return this.target.clId}

	get keepAlive(): boolean {return this.target.keepAlive}

	get wsState(): EWsState {return this.target.wsState}

	postMsg(m: Jsonisable): void {
		(m as JProxiedMsg).u = this.universe;
		this.target.postMsg(m);
	}

	sendMsg(m: Jsonisable): void {
		(m as JProxiedMsg).u = this.universe;
		this.target.sendMsg(m);
	}

	sendReq<R>(m: Jsonisable): Promise<R> {
		(m as JProxiedMsg).u = this.universe;
		return this.target.sendReq(m);
	}

	startWs(): void {
		this.target.startWs();
	}

	listenAuth(authServer: AuthSrv): void {
		//Géré par le l'univers de l'auth
	}

	stopWs(code: number, reason: string): void {
		throw Error("Should never be called in a WsProxyExecFrame !");
	}
}

interface JProxiedMsg {
	u: string
}

export interface JReqRespMsg {
	reqId: number
}

/** Msg de bienvenue du serveur avec des infos sur ce serveur. */
interface JWelcomeMsg {
	buildId: string
}

/** Réponse en attente du serveur. */
interface IRespPending {
	resolve: (value?: Jsonisable | PromiseLike<Jsonisable>) => void
	reject: (reason?: any) => void
}

/** Interne. Construction d'une requête à un svc. */
function buildRequest(servicePath: string, cdaction?: string, params?: string | Dict<any> | URLSearchParams, reqInit?: RequestInit): string {
	let path = servicePath;
	if (params) {
		if (typeof params === 'object' && !(params instanceof URLSearchParams)) {
			const urlParams = new UrlQs();
			for (const key in params) if (params[key] != null) urlParams.append(key, params[key].toString());
			params = urlParams;
		}
		if (reqInit && reqInit.method === 'POST' && reqInit.body == null) {
			//on passe les paramètres en POST.
			if (cdaction) path += '?cdaction=' + cdaction;
			if (!reqInit.headers) reqInit.headers = {};
			(reqInit.headers as any)['Content-type'] = "application/x-www-form-urlencoded";
			reqInit.body = params as any; //Dict<any> fonctionne vraiment ?
		} else {
			if (cdaction) {
				path += '?cdaction=' + cdaction;
				if (params) path += '&' + params;
			} else {
				if (params) path += '?' + params;
			}
		}
	} else if (cdaction) {
		path += '?cdaction=' + cdaction;
	}
	return path;
}

/** Sérialisation json d'un message de log java eu.scenari.commons.log.ILogMsg */
export interface JLogMsg {
	type: 'Halt' | 'Info' | 'Warning' | 'Exception' | 'Error' | 'FatalError'
	desc: string
	vars?: string[]
	details?: string
	ts: number
	user?: string
	appCtx?: string
	thread: string
	parent?: JLogMsg
}

export namespace UNIVERSE {

	export const all: Map<string, Universe> = new Map();
}

//(window as any).UNIVERSE = UNIVERSE; //for DEBUG, window.UNIVERSE.all.get("_auth").wsFrames.ws.stopWs()